import React, {useState} from 'react';
import './TransactionItem.css';
import { userAPI, handleApiError } from "../../../services/api";
import { useTelegram } from '../../../hooks/useTelegram';
import { useTranslation } from 'react-i18next';

const TransactionItem = ({ transaction }) => {
  const { t, i18n } = useTranslation("history");
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(null);
  const {tg} = useTelegram();
  
  const {id, rate_amount, usdt_amount, uzs_amount, operation, created_at, status, usdt_wallet, uzs_card_pan, crypto_hash, ofd_receipt} = transaction;

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleHashCopy = (e) => {
    navigator.clipboard.writeText(crypto_hash)
  };

  const handleOpenOfdReceipt = async () => {
    setLoading(true)
    try{
      const response = await userAPI.getOfdReceipt(id);
      const { link } = response.data;
      if(link){
        if(tg)
        {
          tg.openLink(link)
        } else{
          window.open(link, '_blank');
        }
      }
    }catch(e){
      console.log(handleApiError(e))
    }
    finally{
      setLoading(false)
    }
  };

  const getAmount = () => {
    if(operation?.toLowerCase() === 'buy')
    {
      return "+" + formatNumber(usdt_amount) + " USDT";
    }
    else
    {
      return "+" + formatNumber(uzs_amount) + " UZS";
    }
  };

  const getAntiAmount = () => {
    if(operation?.toLowerCase() === 'buy')
    {
      return formatNumber(uzs_amount) + " UZS";
    }
    else
    {
      return formatNumber(usdt_amount) + " USDT";
    }
  };

  const getAntiAmountStatus = () => {
    if(operation?.toLowerCase() === 'buy')
    {
      return t("SOLD") + ' UZS';
    }
    else
    {
      return t("SOLD") + ' USDT';
    }
  }

  const getType = () => {
    switch (operation?.toLowerCase()){
      case 'buy':
        return t("BUYING") + ' USDT';
      case 'sell':
        return t("SELLING") + ' USDT';
      default: 
        return t("UKNOWN_OPERATION");
    }
  };

  const getDate = () => {
    const uzMonths = [
      "yanvar", "fevral", "mart", "aprel", "may", "iyun",
      "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"
    ];
    let langFormat = "ru-RU"
    const dateObj = new Date(created_at?.replace(" ", "T"));
    switch (i18n.language){
      case "ru":
        break
      case "uz":
        const d = dateObj.getDate();
        const m = uzMonths[dateObj.getMonth()];
        const y = dateObj.getFullYear();
        const h = String(dateObj.getHours()).padStart(2, "0");
        const min = String(dateObj.getMinutes()).padStart(2, "0");
        return `${d} ${m} ${y}, ${h}:${min}`;
      case "en":
        langFormat = "en-US"
        break
      default:
        langFormat = "ru-RU"
    }
    return dateObj.toLocaleString(langFormat, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatus = () => {
    switch (status) {
      case 'NEW':
        return t("IN_PROCESS");
      case 'DONE':
        return t("IS_DONE");
      case 'CANCEL':
        return t("IS_CANCEL");
      default:
        return status;
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'NEW':
        return 'tx-status processing';
      case 'DONE':
        return 'tx-status success';
      case 'CANCEL':
        return 'tx-status error';
      default:
        return 'tx-status';
    }
  };

  return (
    <div>
      <div
        className="transaction-item"
        onClick={() => setExpanded(!expanded)}
        >
        <div className="tx-info"> 
          <div className="tx-type">{getType()}</div> 
          <div className="tx-date">{getDate()}</div> 
        </div> 
        <div className="tx-amount"> 
          <div className="amount default"> {getAmount()} </div> 
          {status && ( <div className={getStatusClass()}> {getStatus()} </div> )} 
        </div> 
      </div>
      {expanded && (
        <div className="tx-extra">
          <div className="tx-row">
            <div className="tx-key">USDT {t("WALLET")}:</div>
            <div className="tx-value">{usdt_wallet?.slice(0, 8) + '...'}</div>
          </div>
          <div className="tx-row">
            <div className="tx-key">UZS {t("BANK_CARD")}:</div>
            <div className="tx-value">{'**** **** **** ' + uzs_card_pan?.slice(12)}</div>
          </div>
          <div className="tx-row">
            <div className="tx-key">{t("OPERATION_HASH")}:</div>
            <div className="tx-value">{crypto_hash?.slice(0, 8) + '...'}</div>
          </div>
          <div className="tx-row">
            <div className="tx-key">{t("EXCHANGE_RATE")} (1 USDT {t("FOR")} UZS):</div>
            <div className="tx-value">{formatNumber(rate_amount) + ' UZS'}</div>
          </div>
          <div className="tx-row">
            <div className="tx-key">{getAntiAmountStatus()}:</div>
            <div className="tx-value">{getAntiAmount()}</div>
          </div>
            {operation === "BUY" && (
              <div className="tx-row">
                <button
                  type="button"
                  onClick={handleOpenOfdReceipt}
                  className="
                    w-full px-4 py-2 rounded-xl 
                    bg-blue-500 text-white text-sm font-medium
                    hover:bg-blue-600 
                    active:bg-blue-700 active:scale-95 
                    focus:outline-none
                    transition-all duration-150
                  "
                >
                  {t("OFD_RECEIPT")}
                </button>
              </div>
            )}
            <div className="tx-row">
              <button
                type="button"
                onClick={handleHashCopy}
                onMouseDown={(e) => e.preventDefault()}
                className="
                  w-full px-4 py-2 rounded-xl 
                  bg-blue-500 text-white text-sm font-medium
                  hover:bg-blue-600 
                  active:bg-blue-700 active:scale-95 
                  focus:outline-none
                  transition-all duration-150
                "
              >
                {t("COPY_OPERATION_HASH")}
              </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default TransactionItem;